import { useId, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { hasRole, useAuthOptional } from '../lib/auth';
import { ActionButton, ErrorBox, Section, useApi } from './kit';

// A/B test of a campaign's first email (src/outbound/ab-test.ts): variant B is the second angle or another subject
// line. Both variants with their numbers, where the winner rule stands, and Start or Stop. Off by default.

type VariantStats = {
  variant: 'A' | 'B'; label: string; subject: string; reached: number; replies: number; interested: number; meetings: number;
  bounced: number; hard_bounced: number; reply_rate: number | null; interested_rate: number | null; bounce_rate: number | null;
};
export type AbTestState = {
  campaign_id: string;
  test: {
    id: string; kind: 'angle' | 'subject'; status: 'running' | 'decided' | 'no_difference' | 'stopped'; winner: 'A' | 'B' | null;
    metric: 'interested' | 'replies' | null; variants: VariantStats[]; text: string;
    verdict: { state: 'not_yet' | 'winner' | 'no_difference'; reason: string; p_b_beats_a: number | null };
    rule: { min_reached: number; max_reached: number; probability: number };
  } | null;
  offer: { available: boolean; recommended: boolean; angles: number; planned_contacts: number; min_contacts: number; note: string };
};

const STATUS: Record<NonNullable<AbTestState['test']>['status'], string> = { running: 'Running', decided: 'Winner chosen', no_difference: 'No clear difference', stopped: 'Stopped' };
const pct = (r: number | null) => (r === null ? '—' : `${(r * 100).toFixed(1)}%`);

export function CampaignAbTest({ campaignId }: { campaignId: string }) {
  const q = useApi<AbTestState>(`/v1/campaigns/${campaignId}/ab-test`);
  const me = useAuthOptional()?.me ?? null;
  const canWrite = !me || hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver', 'rep');
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  if (!d) return null;
  const t = d.test;
  if (!t) return d.offer.available && canWrite ? <StartTest campaignId={campaignId} offer={d.offer} /> : null;
  return (
    <Section title="A/B test of the first email" actions={t.status === 'running' && canWrite
      ? <ActionButton path={`/v1/campaigns/${campaignId}/ab-test/stop`} label="Stop the test" confirm="Stop the test? New contacts get variant A." variant="danger" /> : null}>
      <p className="small"><span className={`chip${t.status === 'decided' ? ' done' : ''}`}>{STATUS[t.status]}</span> {t.text}</p>
      <div className="table-wrap">
        <table className="table">
          <caption className="muted small">Per variant: interested replies per contact reached decide (replies until there are 5 interested); at least {t.rule.min_reached} reached each, at most {t.rule.max_reached}.</caption>
          <thead><tr><th scope="col">Variant</th><th scope="col">Subject</th><th scope="col">Reached</th><th scope="col">Replies</th><th scope="col">Interested</th><th scope="col">Meetings</th><th scope="col">Bounce rate</th></tr></thead>
          <tbody>{t.variants.map((v) => (
            <tr key={v.variant}>
              <th scope="row">{v.label}{t.winner === v.variant && t.status === 'decided' ? <span className="chip done"> winner</span> : null}</th>
              <td>{v.subject}</td><td>{v.reached}</td><td>{v.replies} ({pct(v.reply_rate)})</td><td>{v.interested} ({pct(v.interested_rate)})</td><td>{v.meetings}</td><td>{pct(v.bounce_rate)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Section>
  );
}

/** Off by default: an angle test (two angles) or a subject-line test, before any of the campaign's emails is sent. */
function StartTest({ campaignId, offer }: { campaignId: string; offer: AbTestState['offer'] }) {
  const uid = useId();
  const qc = useQueryClient();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const subject = String(new FormData(ev.currentTarget).get('subject') ?? '').trim();
    setErr(null); setBusy(true);
    try { await api(`/v1/campaigns/${campaignId}/ab-test`, { method: 'POST', body: { kind: 'subject', subject } }); await qc.invalidateQueries(); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <Section title="A/B test of the first email">
      <p className="muted small">{offer.note} Off unless you start it; follow-ups stay the same for both.</p>
      <div className="wg-actions">
        {offer.angles >= 2 ? <ActionButton path={`/v1/campaigns/${campaignId}/ab-test`} body={{ kind: 'angle' }} label="Test the second angle" variant={offer.recommended ? 'primary' : undefined} /> : null}
      </div>
      <form className="wg-form" onSubmit={(e) => void submit(e)}>
        <label htmlFor={`${uid}-s`}>Or test another subject line<input id={`${uid}-s`} name="subject" required maxLength={120} placeholder="e.g. A question about {{company_name}}" /></label>
        <div className="wg-actions"><button type="submit" className="btn" disabled={busy}>{busy ? 'Checking…' : 'Start the subject test'}</button></div>
      </form>
      {err ? <ErrorBox error={err} doing="start the test" /> : null}
    </Section>
  );
}
